import { useState } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { DEFAULT_CLASSIFICATION_TYPES } from "@/types/domain";
import { sportTypeIcons, placeTypeIcons } from "@/lib/sport-icons";
import { IconFilterFilled } from "@tabler/icons-react";

const SPORT_TYPE_LABELS: Record<string, string> = {
  soccer_pitch: "Soccer Pitch",
  tennis_court: "Tennis Court",
  padel_court: "Padel Court",
  basketball_court: "Basketball Court",
  volleyball_court: "Volleyball Court",
  running_track: "Running Track",
  baseball_diamond: "Baseball Diamond",
  rugby_pitch: "Rugby Pitch",
  field_hockey_pitch: "Field Hockey Pitch",
  cricket_pitch: "Cricket Pitch",
  skateboard_park: "Skateboard Park",
  multi_sport_court: "Multi-Sport Court",
  beach_volleyball_court: "Beach Volleyball Court",
  petanque_court: "Petanque Court",
  street_workout_area: "Street Workout Area",
  pickleball_court: "Pickleball Court",
  ping_pong_table: "Ping Pong Table",
  american_football_field: "American Football Field",
  netball_court: "Netball Court",
  handball_court: "Handball Court",
  ice_hockey_rink: "Ice Hockey Rink",
  ultimate_frisbee_field: "Ultimate Frisbee Field",
  disc_golf_course: "Disc Golf Course",
};

const LOCATION_TYPE_LABELS: Record<string, string> = {
  park: "Park",
  playground: "Playground",
  sports_complex: "Sports Complex",
  athletic_field: "Athletic Field",
  stadium: "Stadium",
  sports_club: "Sports Club",
  sports_activity_location: "Sports Activity Location",
  skateboard_park: "Skateboard Park",
  golf_course: "Golf Course",
  swimming_pool: "Swimming Pool",
};

const LOCATION_TYPES = Object.keys(LOCATION_TYPE_LABELS);

export interface MapFilterState {
  classification: "all" | "sport_field" | "no_field" | "unclassified";
  ownership: "all" | "public" | "private" | "institutional" | "unknown";
  reviewStatus: "all" | "unreviewed" | "reviewed" | "rejected";
  source: "all" | "equip_scout" | "city" | "external_import";
  collectionIds: string[];
  enableClustering: boolean;
  sportTypes: string[];
  locationTypes: string[];
  showCourtPins: boolean;
}

interface MapFiltersProps {
  filters: MapFilterState;
  onFiltersChange: (filters: MapFilterState) => void;
}

export function MapFilters({ filters, onFiltersChange }: MapFiltersProps) {
  const { data: collectionsData } = trpc.collections.list.useQuery({});
  const [collectionPopoverOpen, setCollectionPopoverOpen] = useState(false);
  const [sportPopoverOpen, setSportPopoverOpen] = useState(false);
  const [typePopoverOpen, setTypePopoverOpen] = useState(false);

  const toggleCollection = (id: string) => {
    const next = filters.collectionIds.includes(id)
      ? filters.collectionIds.filter((c) => c !== id)
      : [...filters.collectionIds, id];
    onFiltersChange({ ...filters, collectionIds: next });
  };

  const toggleSportType = (type: string) => {
    const current = filters.sportTypes;
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onFiltersChange({ ...filters, sportTypes: next });
  };

  const toggleLocationType = (type: string) => {
    const current = filters.locationTypes;
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onFiltersChange({ ...filters, locationTypes: next });
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-card p-3 shadow-md">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
        Filters
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="clustering-filter"
          checked={filters.enableClustering}
          onCheckedChange={(checked) =>
            onFiltersChange({ ...filters, enableClustering: checked })
          }
        />
        <Label htmlFor="clustering-filter" className="text-sm cursor-pointer">
          Cluster Markers
        </Label>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id="court-pins-filter"
          checked={filters.showCourtPins}
          onCheckedChange={(checked) =>
            onFiltersChange({ ...filters, showCourtPins: checked })
          }
        />
        <Label htmlFor="court-pins-filter" className="text-sm cursor-pointer">
          Show Court Pins
        </Label>
      </div>

      <Select
        value={filters.reviewStatus}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            reviewStatus: value as MapFilterState["reviewStatus"],
          })
        }
      >
        <SelectTrigger size="sm" className="w-[160px]">
          <SelectValue placeholder="Review Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="unreviewed">Unreviewed</SelectItem>
          <SelectItem value="reviewed">Reviewed</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.source}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            source: value as MapFilterState["source"],
          })
        }
      >
        <SelectTrigger size="sm" className="w-[160px]">
          <SelectValue placeholder="Source" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Sources</SelectItem>
          <SelectItem value="equip_scout">Equip Scout</SelectItem>
          <SelectItem value="city">City</SelectItem>
          <SelectItem value="external_import">External Import</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.classification}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            classification: value as MapFilterState["classification"],
          })
        }
      >
        <SelectTrigger size="sm" className="w-[160px]">
          <SelectValue placeholder="Classification" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Classifications</SelectItem>
          <SelectItem value="sport_field">Sport Field</SelectItem>
          <SelectItem value="no_field">No Field</SelectItem>
          <SelectItem value="unclassified">Unclassified</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={filters.ownership}
        onValueChange={(value) =>
          onFiltersChange({
            ...filters,
            ownership: value as MapFilterState["ownership"],
          })
        }
      >
        <SelectTrigger size="sm" className="w-[160px]">
          <SelectValue placeholder="Ownership" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Ownership</SelectItem>
          <SelectItem value="public">Public</SelectItem>
          <SelectItem value="private">Private</SelectItem>
          <SelectItem value="institutional">Institutional</SelectItem>
          <SelectItem value="unknown">Unknown</SelectItem>
        </SelectContent>
      </Select>

      <Popover open={collectionPopoverOpen} onOpenChange={setCollectionPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-[160px] justify-start gap-1.5 text-sm"
          >
            {filters.collectionIds.length === 0
              ? "Collections"
              : filters.collectionIds.includes("__uncollected__")
                ? filters.collectionIds.length === 1
                  ? "No Collection"
                  : `${filters.collectionIds.length} selected`
                : `${filters.collectionIds.length} collection${filters.collectionIds.length !== 1 ? "s" : ""}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent side="left" align="start" className="w-56 p-2">
          {filters.collectionIds.length > 0 && (
            <button
              type="button"
              className="mb-1 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => onFiltersChange({ ...filters, collectionIds: [] })}
            >
              Clear all
            </button>
          )}
          <div className="max-h-64 overflow-y-auto">
            <label className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-accent">
              <Checkbox
                checked={filters.collectionIds.includes("__uncollected__")}
                onCheckedChange={() => toggleCollection("__uncollected__")}
              />
              <span className="text-sm text-muted-foreground italic">No Collection</span>
            </label>
            {collectionsData?.map((collection) => (
              <label
                key={collection.id}
                className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-accent"
              >
                <Checkbox
                  checked={filters.collectionIds.includes(collection.id)}
                  onCheckedChange={() => toggleCollection(collection.id)}
                />
                <span className="text-sm truncate">{collection.name}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={sportPopoverOpen} onOpenChange={setSportPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-[160px] justify-start gap-1.5 text-sm"
          >
            <IconFilterFilled className="size-3.5" />
            {filters.sportTypes.length === 0
              ? "All Sports"
              : `${filters.sportTypes.length} sport${filters.sportTypes.length !== 1 ? "s" : ""}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent side="left" align="start" className="w-56 p-2">
          {filters.sportTypes.length > 0 && (
            <button
              type="button"
              className="mb-1 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => onFiltersChange({ ...filters, sportTypes: [] })}
            >
              Clear all
            </button>
          )}
          <div className="max-h-64 overflow-y-auto">
            {DEFAULT_CLASSIFICATION_TYPES.map((type) => {
              const Icon = sportTypeIcons[type];
              const checked = filters.sportTypes.includes(type);
              return (
                <label
                  key={type}
                  className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-accent"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleSportType(type)}
                  />
                  {Icon && <Icon className="size-3.5 text-muted-foreground" />}
                  <span className="text-sm truncate">
                    {SPORT_TYPE_LABELS[type] ?? type}
                  </span>
                </label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      <Popover open={typePopoverOpen} onOpenChange={setTypePopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-[160px] justify-start gap-1.5 text-sm"
          >
            <IconFilterFilled className="size-3.5" />
            {filters.locationTypes.length === 0
              ? "All Types"
              : `${filters.locationTypes.length} type${filters.locationTypes.length !== 1 ? "s" : ""}`}
          </Button>
        </PopoverTrigger>
        <PopoverContent side="left" align="start" className="w-56 p-2">
          {filters.locationTypes.length > 0 && (
            <button
              type="button"
              className="mb-1 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => onFiltersChange({ ...filters, locationTypes: [] })}
            >
              Clear all
            </button>
          )}
          <div className="max-h-64 overflow-y-auto">
            {LOCATION_TYPES.map((type) => {
              const Icon = placeTypeIcons[type];
              const checked = filters.locationTypes.includes(type);
              return (
                <label
                  key={type}
                  className="flex items-center gap-2 rounded px-2 py-1 cursor-pointer hover:bg-accent"
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleLocationType(type)}
                  />
                  {Icon && <Icon className="size-3.5 text-muted-foreground" />}
                  <span className="text-sm truncate">
                    {LOCATION_TYPE_LABELS[type] ?? type}
                  </span>
                </label>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
